import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const WorkSamples = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const samples = [
    {
      badge: 'IAM · Requirements',
      title: 'Access Review Reconciliation',
      description: 'User story and acceptance criteria for a nightly entitlement reconciliation across Azure AD, Saviynt, and LDAP — the fix that moved audit accuracy from 92% to 98%.',
      client: 'Client A',
      domain: 'IAM Platform',
      color: 'border-l-4 border-blue-600',
      details: [
        { text: 'As a Compliance Officer, I want entitlement data from Azure AD, Saviynt, and LDAP reconciled automatically every night so that I can generate accurate access reports without manual cross-system validation.', highlighted: true },
        { text: 'AC 1: Reconciliation job triggers at 11:00 PM EST nightly with no manual intervention' },
        { text: 'AC 2: Discrepancy > 0 between Azure AD and Saviynt generates dashboard alert within 15 minutes' },
        { text: 'AC 3: LDAP sync failures logged with error code, timestamp, and affected user count in Snowflake' },
        { text: 'AC 4: Power BI reconciliation report available by 6:00 AM EST the following morning' },
        { text: 'DoD: 5 consecutive successful nights in staging · Compliance Lead sign-off · Zero P1/P2 open at release', highlighted: true },
      ],
    },
    {
      badge: 'SQL · Performance',
      title: 'Pipeline Latency Root Cause',
      description: 'Root cause analysis and fix requirements for a Power BI pipeline refreshing every 22 minutes. Reduced to 13 minutes through SQL and incremental load redesign.',
      client: 'Client A',
      domain: 'Snowflake + Power BI',
      color: 'border-l-4 border-teal-600',
      details: [
        { text: 'Root cause 1: Full table scan on audit_events (450M+ rows) — no date filter applied on every refresh', highlighted: true },
        { text: 'Root cause 2: Three-way JOIN with no index optimization pulling all historical records', highlighted: true },
        { text: 'Root cause 3: No incremental watermark — entire dataset reprocessed on each pipeline run', highlighted: true },
        { text: 'Fix req 1: Add last_processed_timestamp watermark to pipeline config' },
        { text: 'Fix req 2: Filter event_date >= DATEADD(day, -90, CURRENT_DATE()) before any JOIN' },
        { text: 'Fix req 3: Materialized summary table for top 10 KPIs, refresh every 4 hours' },
        { text: 'Result: 22 min → 13 min (40% improvement). Business stakeholders confirmed impact.', highlighted: true },
      ],
    },
    {
      badge: 'Process · Gap Analysis',
      title: 'Access Request Workflow Redesign',
      description: 'As-is process had no SLA — requests took 3–14 days. Designed to-be with 48h SLA, parallel approvals, and full audit trail. Full diagram in the next section.',
      client: 'Client A',
      domain: 'Saviynt + Jira',
      color: 'border-l-4 border-cyan-600',
      details: [
        { text: 'Pain 1: Email-chain approvals — avg 2 days before a ticket was even created', highlighted: true },
        { text: 'Pain 2: Sequential approvals across 3 teams — no parallelism, no SLA enforcement', highlighted: true },
        { text: 'Pain 3: Approvals captured in email threads — no system of record, 40% audit completeness', highlighted: true },
        { text: 'To-be: Self-service Saviynt portal → auto-ticket in 5 min → parallel Manager + Security approval' },
        { text: 'SLA: 48h standard · 4h for P1 · Escalation email auto-fires at 24h if unactioned' },
        { text: 'Outcome targets: 14 days → 48h · Audit completeness 40% → 100% · Req ambiguity –35%', highlighted: true },
      ],
    },
    {
      badge: 'WMS · Defect Reduction',
      title: 'Order Defect Root Cause — Client B',
      description: '30% order defect rate across WMS fulfillment. Gap analysis, SQL exception logic requirements, and UAT test cases that reduced defects 30% and inventory mismatches 25%.',
      client: 'Client B',
      domain: 'WMS + BI Systems',
      color: 'border-l-4 border-indigo-600',
      details: [
        { text: 'Gap 1: No exception handling at WMS → BI handoff — failed records silently dropped', highlighted: true },
        { text: 'Gap 2: 12 mismatched order status codes between WMS and fulfillment system', highlighted: true },
        { text: 'Gap 3: No reconciliation between WMS pick confirmation and fulfillment shipped record', highlighted: true },
        { text: 'TC-01: Pick confirmed with no matching fulfillment_shipped → exception logged within 60s' },
        { text: 'TC-02: Status code PART_SHIP maps to PARTIAL_FULFILLMENT in downstream system' },
        { text: 'Result: Order defects –30% · Inventory mismatches –25% · SLA breaches eliminated in 2 months', highlighted: true },
      ],
    },
  ];

  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section id="work" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-800 border-blue-200 font-semibold">Portfolio</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Work Samples
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl">
              Real-world examples of requirements gathering, process optimization, and data-driven solutions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {samples.map((sample, index) => (
              <Card key={index} className={`${sample.color} bg-white hover:shadow-lg transition-all duration-300 border border-slate-200`}>
                <CardContent className="p-6">
                  <Badge variant="outline" className="mb-3 text-xs font-semibold border-slate-300">{sample.badge}</Badge>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{sample.title}</h3>
                  <p className="text-slate-600 mb-4 text-sm leading-relaxed">{sample.description}</p>
                  
                  <div className="flex gap-4 text-xs text-slate-500 mb-4">
                    <span><strong className="text-slate-700">Client:</strong> {sample.client}</span>
                    <span><strong className="text-slate-700">Domain:</strong> {sample.domain}</span>
                  </div>

                  <Button
                    onClick={() => toggleExpand(index)}
                    variant="outline"
                    size="sm"
                    className="w-full justify-between hover:bg-slate-50 border-slate-300"
                  >
                    <span className="text-sm font-medium">{expandedIndex === index ? 'Hide details' : 'View details'}</span>
                    {expandedIndex === index ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </Button>

                  {expandedIndex === index && (
                    <div className="mt-4 pt-4 border-t border-slate-200 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                      {sample.details.map((detail, idx) => (
                        <div
                          key={idx}
                          className={`text-sm flex items-start gap-2 ${
                            detail.highlighted
                              ? 'bg-blue-50 border-l-4 border-blue-600 pl-3 py-2 rounded-r font-medium text-slate-800'
                              : 'text-slate-600'
                          }`}
                        >
                          {!detail.highlighted && <span className="text-blue-600 mt-1">•</span>}
                          <span>{detail.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkSamples;